<template>
  <div v-if="items.length"
       class="tracking-timeline">
    <div class="card-title-row">
      <h5 class="header-text text-color-4 mb-0">{{ $t("messenger.tracking.timeline_title") }}</h5>
      <router-link :to="{ name: 'Seller-Settings-Shipping' }"
                   class="btn btn-outline-primary btn-sm">
        <i class="fab fa-facebook-messenger mr-1"></i>{{ $t("messenger.tracking.open_settings") }}
      </router-link>
    </div>

    <ul class="tl">
      <li v-for="item in items"
          :key="item.key"
          :class="'--' + item.kind">
        <span class="tl-dot"><i :class="item.icon"></i></span>
        <div>
          <div class="tl-label"
               :title="item.detail || null">{{ item.label }}</div>
          <div v-if="item.time"
               class="tl-time">{{ item.time }}</div>
          <div v-if="item.sub"
               class="tl-sub">{{ item.sub }}</div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { thDateTime, failureReason, failureDetail } from "@/components/derived/messenger/format";

// Tracking messages proship-messenger sent for one order (GET /api/orders/:orderId/tracking-messages).
// Fetches on its own and renders nothing until there is at least one message, so the order page keeps
// its previous content when Messenger has no rows or cannot be reached. Emits "loaded" with the count.
export default {
  name: "TrackingTimeline",
  props: {
    orderId: { type: String, required: true }, // bare Proship order id (the part before "|")
  },

  data() {
    return { messages: [], request: 0 };
  },

  mounted() {
    this.fetch();
  },

  watch: {
    orderId() {
      this.fetch();
    },
  },

  computed: {
    items() {
      const t = (key, params) => this.$t("messenger.tracking." + key, params);
      const sorted = this.messages.slice().sort((a, b) => (a.sentAt || 0) - (b.sentAt || 0) || (a.id || 0) - (b.id || 0));
      const items = [];

      sorted.forEach((m, index) => {
        const key = (suffix) => (m.id != null ? m.id : index) + "-" + suffix;
        const sub = m.trackingNo ? t("tracking_no", { no: m.trackingNo }) : "";

        if (m.status === "failed") {
          items.push({
            key: key("failed"),
            kind: "fail",
            icon: "fas fa-times",
            label: failureReason(m) ? t("failed_reason", { reason: failureReason(m) }) : t("failed"),
            detail: failureDetail(m),
            time: thDateTime(m.sentAt),
            sub: m.event === "cancelled" ? t("kind_cancel") : sub,
          });
          return;
        }

        if (m.status !== "sent") {
          items.push({ key: key("wait"), kind: "wait", icon: "fal fa-hourglass-half", label: t("sending"), time: thDateTime(m.sentAt), sub });
          return;
        }

        items.push({
          key: key("sent"),
          kind: "done",
          icon: "fas fa-check",
          label: m.event === "cancelled" ? t("cancel_sent") : t("sent_at"),
          time: thDateTime(m.sentAt),
          sub,
        });
        if (m.deliveredAt) {
          items.push({ key: key("delivered"), kind: "done", icon: "fas fa-check", label: t("delivered"), time: thDateTime(m.deliveredAt) });
        }
        if (m.readAt) {
          items.push({ key: key("read"), kind: "done", icon: "fas fa-check-double", label: t("read"), time: thDateTime(m.readAt) });
        } else {
          items.push({ key: key("unread"), kind: "todo", icon: "fal fa-circle", label: t("unread") });
        }
      });

      return items;
    },
  },

  methods: {
    fetch() {
      const request = ++this.request;
      this.messages = [];
      if (!this.orderId) {
        this.$emit("loaded", 0);
        return;
      }
      this.$store.dispatch("messenger/fetchOrderMessages", {
        orderId: this.orderId,
        callback: (ok, messages) => {
          if (request !== this.request) return;
          this.messages = ok && Array.isArray(messages) ? messages : [];
          this.$emit("loaded", this.messages.length);
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.tl {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    position: relative;
    display: flex;
    gap: 0.85rem;
    padding-bottom: 1.1rem;

    &:last-child {
      padding-bottom: 0;
    }

    &:not(:last-child)::after {
      content: "";
      position: absolute;
      left: 11px;
      top: 26px;
      bottom: 2px;
      width: 2px;
      background: #e6e6e6;
    }

    &.--done:not(:last-child)::after {
      background: #bfe9c8;
    }

    &.--done .tl-dot {
      background: rgba($brand-green-mid, 0.14);
      color: $brand-green-mid;
    }

    &.--fail .tl-dot {
      background: rgba($brand-pink, 0.15);
      color: $brand-pink;
    }

    &.--wait .tl-dot {
      background: rgba(255, 152, 0, 0.12);
      color: #ff9800;
    }

    &.--todo .tl-label {
      color: map-get($text-colors, 5);
    }

    &.--fail .tl-label {
      color: #d65c5c;
    }
  }

  .tl-dot {
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    background: $dashboard-background;
    color: map-get($text-colors, 5);
    position: relative;
    z-index: 1;
  }

  .tl-label {
    color: map-get($text-colors, 2);
    font-size: 1rem;
  }

  .tl-time {
    color: map-get($text-colors, 4);
    font-size: 0.875rem;
    margin-top: 0.05rem;
  }

  .tl-sub {
    color: map-get($text-colors, 5);
    font-size: 0.8125rem;
    margin-top: 0.15rem;
  }
}

body.--dark .tl .tl-label {
  color: lightgray;
}
</style>
